import { useGetClusterInfiniteQuery } from "@/components/ContentTabs/clustersTab/ClusterTableLayout/hook/useGetClusterInfiniteQuery"
import { TelegramAltIcon } from "@/components/Icons"
import { RequiredErrorMessage } from "@/components/RequiredErrorMessage"
import SingleField from "@/components/Sections/SingleField"
import { ClusterStatus } from "@/domain/cluster/constants"
import { useGenericErrorToast } from "@/hooks/useGenericErrorToast"
import {
  type ClusterJsonldReadItemClusterItem,
  getGetUserSharedDatasQueryKey,
  usePostInterbu,
} from "@/networking/api"
import { useGetReactSelectStyles } from "@/theme/components-style-overrides/reactselect-definition"
import { Orders } from "@/utils/filters/types"
import { isNotEmptyArray, isNotNullNorUndefined } from "@/utils/ts-utils"
import { Box, Button, Flex, FormControl, Textarea } from "@chakra-ui/react"
import { useQueryClient } from "@tanstack/react-query"
import { useMemo } from "react"
import { Controller, useForm } from "react-hook-form"
import { FormattedMessage, useIntl } from "react-intl"
import ReactSelect from "react-select"

import { normalizedNameFilter } from "@/utils/search/matchesNormalizedName"
import { NearbyClusterSuggestions } from "../../components/NearbyClusterSuggestions"
import { useCreationMultiStepFormContext } from "../../context/ShareRequestInterbuCreateModalContext"
import { specialisedClustersFor } from "../../helpers/nearbyClusters"

type StepForm = {
  clusters: string[]
  message: string
}

export default ({ onClose }: { onClose: () => void }) => {
  const intl = useIntl()
  const {
    formState,
    setFormState,
    storeValues,
    setValuesSelected,
    storeSearchTerms,
    setSearchTerm,
    addressLocation,
  } = useCreationMultiStepFormContext()

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<StepForm>({
    defaultValues: {
      clusters: formState.clusters ?? [],
      message: formState.message ?? "",
    },
  })

  // Clusters request / options

  const searchTermCluster = useMemo(() => storeSearchTerms.clusters ?? "", [storeSearchTerms])

  const {
    clusters,
    isFetchingData: isLoadingClusters,
    fetchNextPage,
    hasNextPage,
  } = useGetClusterInfiniteQuery({
    search: searchTermCluster,
    itemsPerPage: 20,
    "status.code": ClusterStatus.ACTIVE,
    "order[name]": Orders.ASC,
    // Never an agency only typed "physical" (ADR 0017, D4).
    notPhysicalOnly: true,
    withMinProps: true,
  })
  const clusterOptions = useMemo(
    () =>
      (clusters ?? [])
        // The sender agency is never a recipient (ADR 0004, D4; refused by the API too).
        .filter(c => c.id !== formState.from_cluster_id)
        .map(c => {
          return {
            value: c,
            label: c.name ?? "unknown",
          }
        }),
    [clusters, formState.from_cluster_id]
  )
  const clusterOptionSelected = useMemo(() => {
    const clustersSelected = (storeValues?.clusters ?? undefined) as
      | ClusterJsonldReadItemClusterItem[]
      | undefined
    return isNotEmptyArray(clustersSelected)
      ? clustersSelected.map(c => ({
          value: c,
          label: c.name ?? "unknown",
        }))
      : []
  }, [storeValues])

  const selectedClusterIds = useMemo(
    () => clusterOptionSelected.map(option => option.value?.id ?? ""),
    [clusterOptionSelected]
  )

  // A nearby suggestion adds or removes the agency, like the select does.
  const toggleCluster = (cluster: { id?: string; name?: string | null }) => {
    const isSelected = selectedClusterIds.includes(cluster.id ?? "")
    const nextOptions = isSelected
      ? clusterOptionSelected.filter(option => option.value?.id !== cluster.id)
      : [
          ...clusterOptionSelected,
          { value: cluster as ClusterJsonldReadItemClusterItem, label: cluster.name ?? "unknown" },
        ]
    setValuesSelected("clusters", nextOptions)
    setValue(
      "clusters",
      nextOptions.map(option => option.value?.id ?? ""),
      { shouldValidate: true }
    )
  }

  let updateClusterSearchItv: NodeJS.Timeout
  const onClusterInputChange = (text: string) => {
    if (searchTermCluster === text) return
    if (isNotNullNorUndefined(updateClusterSearchItv)) clearTimeout(updateClusterSearchItv)
    updateClusterSearchItv = setTimeout(() => setSearchTerm("clusters", text), 500)
  }

  // Last step: sends the InterBU (ADR 0004 D1).
  const queryClient = useQueryClient()
  const genericErrorToast = useGenericErrorToast()
  const { mutate, isLoading: isLoadingPost } = usePostInterbu()
  const onSubmit = (stepFormData: StepForm) => {
    const postData = {
      ...formState,
      ...stepFormData,
    }
    setFormState(postData)

    mutate(
      { data: postData },
      {
        onSuccess: () => {
          // Every shared-data list (client sheet, Shares page) shows the new InterBU.
          queryClient.invalidateQueries({ queryKey: getGetUserSharedDatasQueryKey() })
          onClose()
        },
        onError: genericErrorToast,
      }
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)} maxW="100%" margin="auto">
      <Flex direction="column" gap="5" my="8">
        <SingleField
          label={intl.formatMessage({
            id:
              specialisedClustersFor(formState.type, formState.country) === undefined
                ? "userSharedData.interbu.modal.steps.2.nearby"
                : "userSharedData.interbu.modal.steps.2.specialised",
          })}
        >
          <NearbyClusterSuggestions
            location={addressLocation}
            activity={formState.type}
            country={formState.country}
            excludedClusterId={formState.from_cluster_id}
            selectedIds={selectedClusterIds}
            onToggle={toggleCluster}
          />
        </SingleField>
        <SingleField
          label={intl.formatMessage({
            id: "userSharedData.interbu.modal.steps.2.clusterRecipient",
          })}
          required
        >
          <FormControl isInvalid={!!errors.clusters}>
            <Controller
              control={control}
              name="clusters"
              rules={{ required: true }}
              render={({ field: { value, onChange, onBlur, ref }, fieldState }) => (
                <ReactSelect
                  ref={ref}
                  onBlur={onBlur}
                  isMulti={true}
                  filterOption={normalizedNameFilter}
                  options={clusterOptions}
                  value={clusterOptionSelected}
                  placeholder={intl.formatMessage({
                    id: "userSharedData.interbu.modal.steps.2.clusterRecipient.placeholder",
                  })}
                  // react-select disables scroll capture on touch devices, and with it
                  // onMenuScrollToBottom: force it so the next page also loads on tablets.
                  captureMenuScroll={true}
                  onMenuScrollToBottom={() => {
                    if (hasNextPage) fetchNextPage()
                  }}
                  onChange={newVal => {
                    const newIds = [...newVal.values()].map(c => c.value?.id)
                    onChange(newIds)
                    setValuesSelected("clusters", [...newVal])
                  }}
                  onInputChange={onClusterInputChange}
                  styles={useGetReactSelectStyles(fieldState)}
                  isLoading={isLoadingClusters}
                  loadingMessage={() => intl.formatMessage({ id: "loading.generic.title" })}
                />
              )}
            />
            <RequiredErrorMessage />
          </FormControl>
        </SingleField>
        <SingleField
          label={intl.formatMessage({ id: "userSharedData.interbu.modal.steps.2.description" })}
        >
          <Textarea {...register("message")} />
        </SingleField>
      </Flex>

      <Flex justifyContent="center" mt="100px" my={10}>
        <Button display="flex" gap="2" type="submit" w="300px" isLoading={isLoadingPost}>
          <FormattedMessage id="userSharedData.modal.sendRequest" />
          <TelegramAltIcon boxSize={4} />
        </Button>
      </Flex>
    </Box>
  )
}
